// Open tasks: one block for the v2 deal card and the three phone pages
// (record-page redesign step 15). window.RecordTasks + window.RecordTasksKit.
//
//   kind          'deal' | 'company' | 'contact'
//   presentation  'card'  a v2 Section (the deal page's Open tasks card, moved here; it stays
//                         while only completed tasks remain: Open tasks · 0 + Completed · N,
//                         and at none for a writer: Open tasks · 0, a dimmed —, + Task)
//                 'phone' a phone section header + 56px rows with a 44px check
// Data:
//   deal     the page's `tasks` (DealDetail: 50 rows, both statuses) + onTasksChange
//   company  its own open list (Retry on a failed load), re-read on 'task-created' and `tick`
//   contact  window.useContactOpenTasks (views/contact_detail.jsx), re-read the same way
// RecordTasks has no hooks: it picks a source, and each source owns its hooks.
// A check completes (a recurring task schedules its next one: "Done · next …", Undo);
// a row opens the task (TaskDrawer: a full-screen sheet on a phone).

const RT_SHOWN = 5;
// Overdue first (in list order), then the other open tasks, then the completed ones apart.
const rtSplit = (tasks, today) => {
  const open = (tasks || []).filter((t) => t.status !== 'completed');
  const overdue = open.filter((t) => t.due_date && String(t.due_date).slice(0, 10) < today);
  const rest = open.filter((t) => !overdue.includes(t));
  return { open: [...overdue, ...rest], overdue: overdue.length, done: (tasks || []).filter((t) => t.status === 'completed') };
};
// TaskDrawer's initialLinks for a new task on this record ({ id, name } each).
const rtLinks = (kind, r) => {
  const x = r || {};
  if (kind === 'deal') {
    const d = x.deal || {};
    return { engagement: d.id ? { id: d.id, name: d.name || '' } : null, company: d.target ? { id: d.target, name: d.targetName || '' } : null };
  }
  if (kind === 'company') return { company: x.companyId ? { id: x.companyId, name: x.companyName || '' } : null };
  if (kind === 'contact') {
    const c = x.contact || {}; const raw = c._raw || {};
    return { contact: x.contactId ? { id: x.contactId, name: c.name || '' } : null,
      company: raw.company_id ? { id: raw.company_id, name: raw.company_name || c.company || '' } : null };
  }
  return {};
};
// The phone row's second line: due (or overdue), whose, and where from.
const rtMeta = (kind, t, today, fmtDue) => {
  const due = t.due_date ? String(t.due_date).slice(0, 10) : null;
  const overdue = !!due && due < today;
  return [due ? `${overdue ? 'Overdue · ' : 'Due '}${fmtDue ? fmtDue(due) : due}` : null,
    kind === 'contact' && !t.contact_id ? 'Company-wide' : null,
    kind === 'company' ? t.engagement_name || null : null,
    t.assigned_to_name || null].filter(Boolean).join(' · ');
};
const RecordTasksKit = Object.freeze({ split: rtSplit, links: rtLinks, meta: rtMeta, SHOWN: RT_SHOWN });
window.RecordTasksKit = RecordTasksKit;

const rtLink = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit', textUnderlineOffset: 2 };
const rtTap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// ── card (v2 deal page): the Open tasks card as deal_page_v2.jsx drew it, kept while ─
// only completed tasks remain (classic's list showed them; v2 had no other place), and
// with none at all for whoever can add one (+ Task); a viewer or guest then sees nothing.
const RecordTasksCard = ({ tasks, users, activeUser, canWrite, onTasksChange, onCompose, sectionId }) => {
  const toggle = window.useTaskToggle(onTasksChange);
  const [editing, setEditing] = React.useState(null);
  const today = window.localISODate();
  const split = rtSplit(tasks, today);
  const none = split.open.length === 0 && split.done.length === 0;
  const canAdd = !!(canWrite && onCompose);
  if (!tasks || (none && !canAdd)) return null;
  const row = (t, done) => {
    const ps = toggle.stateFor(t.id);
    const checked = done || !!ps;
    const lingering = ps && ps.phase === 'done';
    const overdue = !checked && t.due_date && String(t.due_date).slice(0, 10) < today;
    const pHue = t.priority === 'urgent' ? 'rose' : t.priority === 'high' ? 'amber' : t.priority === 'low' ? 'slate' : 'blue';
    return (
      <div key={t.id} data-task-row={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 14px', borderTop: '1px solid var(--border)', background: lingering ? 'var(--green-bg)' : 'transparent', opacity: done ? 0.55 : 1 }}>
        <button type="button" disabled={!canWrite} aria-label={checked ? 'Completed' : 'Mark complete'} onClick={() => { if (canWrite) toggle.toggle(t); }}
          style={{ width: 14, height: 14, borderRadius: 4, marginTop: 2, border: checked ? 'none' : '1.5px solid var(--border-strong)', background: checked ? 'oklch(0.55 0.12 155)' : 'var(--surface)', cursor: canWrite ? 'pointer' : 'default', padding: 0, flexShrink: 0, color: '#fff', fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{checked ? '✓' : ''}</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <button type="button" onClick={window.TaskDrawer ? () => setEditing(t) : undefined} title={t.title}
            style={{ ...rtLink, display: 'block', width: '100%', textAlign: 'left', fontSize: 12.5, fontWeight: 500, color: 'var(--text)', textDecoration: checked ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.title}{(t.recurrence_text || t.recurrence) ? <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-3)' }}>↻ {t.recurrence_text || 'repeats'}</span> : null}
          </button>
          {lingering ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: 'var(--green-fg)' }}>{ps.spawnedDue ? `Done · next ${window.fmtTaskDue(ps.spawnedDue)}` : 'Done'}</span>
              <window.Btn size="xs" variant="outline" onClick={() => toggle.undo(t)}>Undo</window.Btn>
            </div>
          ) : !done && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11 }}>
              <window.Pill hue={pHue} sz="sm">{t.priority}</window.Pill>
              <span style={{ color: overdue ? 'var(--rose-fg)' : 'var(--text-3)' }}>{overdue ? `overdue · ${t.due_date}` : t.due_date}</span>
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      <window.Section title={`Open tasks · ${split.open.length}`} collapsible id={sectionId}
        preview={`${split.open.length} open · ${split.overdue} overdue`}
        action={canAdd ? <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={() => onCompose('task')}>Task</window.Btn> : null}>
        <div style={{ marginTop: -1 }}>
          {none ? <div data-task-empty="" style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-4)' }}>—</div>
            : split.open.slice(0, RT_SHOWN).map((t) => row(t, false))}
        </div>
        {split.done.length > 0 && (
          <details style={{ borderTop: '1px solid var(--border)' }}>
            <summary style={{ padding: '6px 14px', cursor: 'pointer', fontSize: 11, color: 'var(--text-3)' }}>Completed · {split.done.length}</summary>
            {split.done.map((t) => row(t, true))}
          </details>
        )}
      </window.Section>
      {editing && window.TaskDrawer && (
        <window.TaskDrawer task={editing} members={users} activeUser={activeUser} onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); onTasksChange(); }} />
      )}
    </>
  );
};

// ── phone: every open task (overdue first), Completed · N where loaded, Add ──
const RecordTasksPhone = ({ kind, tasks, error, onRetry, reload, users, activeUser, canWrite, links }) => {
  const toggle = window.useTaskToggle(reload);
  const [editing, setEditing] = React.useState(null);   // a task, or { create: true }
  const today = window.localISODate();
  const split = rtSplit(tasks, today);
  const row = (t, done) => {
    const ps = toggle.stateFor(t.id);
    const checked = done || !!ps;
    const lingering = ps && ps.phase === 'done';
    const overdue = !checked && t.due_date && String(t.due_date).slice(0, 10) < today;
    const urgent = !done && (t.priority === 'urgent' || t.priority === 'high');
    return (
      <div key={t.id} data-task-row={t.id} style={{ display: 'flex', alignItems: 'center', background: lingering ? 'var(--green-bg)' : 'var(--surface)',
        borderBottom: '1px solid var(--border)', opacity: done ? 0.6 : 1 }}>
        <button type="button" disabled={!canWrite} aria-label={checked ? 'Undo complete' : `Complete “${t.title}”`}
          onClick={() => { if (canWrite) toggle.toggle(ps ? { ...t, status: 'completed' } : t); }}
          style={{ ...rtTap, width: 52, height: 56, flexShrink: 0, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canWrite ? 'pointer' : 'default' }}>
          <span style={{ width: 22, height: 22, borderRadius: 11, border: `2px solid ${checked ? 'var(--accent)' : 'var(--border-strong)'}`, background: checked ? 'var(--accent)' : 'transparent',
            color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', animation: ps ? 'atlas-check-pop .25s ease' : 'none' }}>
            {checked && <window.Icon.Check size={13} />}
          </span>
        </button>
        <button type="button" disabled={!canWrite} onClick={canWrite && window.TaskDrawer ? () => setEditing(t) : undefined}
          style={{ ...rtTap, flex: 1, minWidth: 0, minHeight: 56, padding: '9px 16px 9px 0', border: 'none', background: 'transparent', textAlign: 'left',
                   cursor: canWrite && window.TaskDrawer ? 'pointer' : 'default', color: 'var(--text)', fontFamily: 'inherit' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 15.5, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                           textDecoration: checked ? 'line-through' : 'none', color: checked ? 'var(--text-3)' : 'var(--text)' }}>{t.title}</span>
            {urgent && <window.Pill hue={t.priority === 'urgent' ? 'rose' : 'amber'} sz="sm">{t.priority}</window.Pill>}
          </span>
          <span style={{ display: 'block', fontSize: 13, marginTop: 2, color: overdue ? 'var(--rose-fg)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lingering && ps.spawnedDue ? `Done · next ${window.fmtTaskDue(ps.spawnedDue)}` : lingering ? 'Done' : rtMeta(kind, t, today, window.fmtTaskDue)}
          </span>
        </button>
        {lingering && (
          <span style={{ paddingRight: 12, flexShrink: 0 }}><window.Btn size="sm" variant="outline" onClick={() => toggle.undo(t)}>Undo</window.Btn></span>
        )}
      </div>
    );
  };
  return (
    <>
      <window.PhoneSectionHeader title="Open tasks" count={tasks ? split.open.length : null}
        action={canWrite && window.TaskDrawer ? (
          <button type="button" disabled={!activeUser} onClick={() => setEditing({ create: true })}
            style={{ ...rtTap, minWidth: 44, height: 44, margin: '-12px -4px -12px 0', padding: '0 4px', border: 'none', background: 'transparent',
                     color: activeUser ? 'var(--accent-text)' : 'var(--text-4)', fontSize: 15, fontWeight: 600, cursor: activeUser ? 'pointer' : 'default' }}>Add</button>
        ) : null} />
      <div data-record-tasks={kind} style={{ borderTop: '1px solid var(--border)' }}>
        {tasks === null && window.SkeletonRows && <window.SkeletonRows rows={2} />}
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, padding: '6px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', fontSize: 14, color: 'var(--rose-fg)' }}>
            <span style={{ flex: 1, minWidth: 0 }}>{error}</span>
            {onRetry && <window.Btn size="sm" variant="outline" onClick={onRetry}>Retry</window.Btn>}
          </div>
        )}
        {split.open.map((t) => row(t, false))}
        {split.done.length > 0 && (
          <details style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <summary style={{ ...rtTap, display: 'flex', alignItems: 'center', minHeight: 48, padding: '0 16px', cursor: 'pointer', fontSize: 14, color: 'var(--text-3)' }}>
              Completed · {split.done.length}
            </summary>
            {split.done.map((t) => row(t, true))}
          </details>
        )}
      </div>
      {editing && window.TaskDrawer && (
        <window.TaskDrawer activeUser={activeUser} members={users} task={editing.create ? undefined : editing}
          initialLinks={editing.create ? links : undefined}
          onClose={() => setEditing(null)}
          onCreated={() => { reload(); window.dispatchEvent(new CustomEvent('task-created')); }} />
      )}
    </>
  );
};

// ── the sources ───────────────────────────────────────────────────────────────
const rtView = (p, data) => (p.presentation === 'phone'
  ? <RecordTasksPhone kind={p.kind} users={p.users} activeUser={p.activeUser} canWrite={!!p.canWrite} links={rtLinks(p.kind, p)} {...data} />
  : <RecordTasksCard tasks={data.tasks} users={p.users} activeUser={p.activeUser} canWrite={!!p.canWrite}
      onTasksChange={data.reload} onCompose={p.onCompose} sectionId={p.sectionId || `${p.kind}.tasks`} />);

// A record's own open list (company): first load per id, a re-read keeps the rows
// on screen (a just-checked task's linger isn't unmounted mid-way).
const CompanyTasksSource = (p) => {
  const [tasks, setTasks] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [n, setN] = React.useState(0);
  const reload = React.useCallback(() => setN((x) => x + 1), []);
  React.useEffect(() => { setTasks(null); }, [p.companyId]);
  React.useEffect(() => {
    if (!p.companyId) { setTasks([]); return undefined; }
    let alive = true;
    window.AtlasApi.listTasks({ company_id: p.companyId, status: 'open', limit: 50 })
      .then((res) => { if (alive) { setTasks((res && res.items) || []); setError(null); } })
      .catch((e) => { if (alive) { setTasks([]); setError(e.message || 'Could not load tasks'); } });
    return () => { alive = false; };
  }, [p.companyId, p.tick, n]);
  React.useEffect(() => {
    window.addEventListener('task-created', reload);
    return () => window.removeEventListener('task-created', reload);
  }, [reload]);
  return rtView(p, { tasks, error, onRetry: reload, reload });
};

const ContactTasksSource = (p) => {
  const raw = (p.contact && p.contact._raw) || {};
  const { tasks, error, reload } = window.useContactOpenTasks(raw.company_id || null, p.contactId);
  React.useEffect(() => {
    window.addEventListener('task-created', reload);
    return () => window.removeEventListener('task-created', reload);
  }, [reload]);
  React.useEffect(() => { if (p.tick) reload(); }, [p.tick]);   // eslint-disable-line react-hooks/exhaustive-deps
  return rtView(p, { tasks, error, onRetry: reload, reload });
};

const RecordTasks = (p) => {
  if (p.kind === 'company') return <CompanyTasksSource {...p} />;
  if (p.kind === 'contact') return window.useContactOpenTasks ? <ContactTasksSource {...p} /> : null;
  return rtView(p, { tasks: p.tasks, error: null, onRetry: null, reload: p.onTasksChange || (() => {}) });
};

window.RecordTasks = RecordTasks;
